/**
 * WorkshopReviewsSection — Phase 4c+++ (Feb 2026).
 *
 * Trust-Signal: Zeigt veröffentlichte Werkstatt-Bewertungen auf dem
 * Dealer-Profil. Daten kommen vom public-Endpoint
 * `GET /api/public/dealers/{dealer_id}/workshop-reviews`.
 */
import { useEffect, useState } from "react";
import { Star, Wrench, MessageSquare } from "lucide-react";
import api from "../lib/api";

function StarRow({ value }) {
    return (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
                <Star
                    key={s}
                    className={`w-4 h-4 ${s <= value ? "fill-[#FFCC00] text-[#FFCC00]" : "text-gray-300"}`}
                />
            ))}
        </div>
    );
}

export default function WorkshopReviewsSection({ dealerId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get(`/public/dealers/${dealerId}/workshop-reviews?limit=10`);
                if (!cancelled) setData(r.data);
            } catch (_) {
                if (!cancelled) setData(null);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [dealerId]);

    if (loading) return null;
    if (!data || data.count === 0) return null;

    return (
        <section className="mt-10" data-testid="workshop-reviews-trust-section">
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <div className="bg-[#111827] text-white px-5 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Wrench className="w-6 h-6 text-[#FFCC00]" />
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-400">Trust-Signal</div>
                            <h3 className="font-display font-black text-2xl">Werkstatt-Bewertungen</h3>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="font-mono-num font-black text-3xl text-[#FFCC00]">
                                {data.average !== null ? data.average.toFixed(1) : "—"}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-400">
                                aus {data.count} Bewertung{data.count !== 1 ? "en" : ""}
                            </div>
                        </div>
                        <Star className="w-8 h-8 fill-[#FFCC00] text-[#FFCC00]" />
                    </div>
                </div>
                <p className="px-5 py-2 text-[11px] text-gray-500 bg-gray-50 border-b border-gray-100" data-testid="workshop-reviews-authenticity-note">
                    Echtheit: Diese Bewertungen werden nach einem <b>abgeschlossenen Werkstattauftrag</b> erhoben und vor der Veröffentlichung geprüft.
                </p>
                <div className="divide-y divide-gray-100">
                    {(data.reviews || []).map((r) => (
                        <div key={r.id} className="px-5 py-4" data-testid={`workshop-review-${r.id}`}>
                            <div className="flex items-center gap-3">
                                <StarRow value={r.rating} />
                                <span className="font-mono-num font-black text-[#111827]">{r.rating}/5</span>
                                <span className="text-xs text-gray-500 ml-auto">
                                    {r.published_at ? new Date(r.published_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : ""}
                                </span>
                            </div>
                            {r.comment && (
                                <blockquote className="mt-2 pl-4 border-l-2 border-gray-300 text-sm text-gray-800 italic">
                                    „{r.comment}"
                                </blockquote>
                            )}
                            {r.dealer_response && (
                                <div className="mt-3 bg-gray-50 border-l-4 border-[#FFCC00] px-3 py-2 text-sm">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600 mb-0.5 flex items-center gap-1">
                                        <MessageSquare className="w-3 h-3" /> Antwort des Händlers
                                    </div>
                                    <div className="text-gray-800">{r.dealer_response}</div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
